/** The record label and the picture-disc variant: album art printed on vinyl. */

import type * as THREE from 'three';
import { TAU } from '../lib/math.ts';
import type { Palette } from '../lib/palette.ts';
import { canvas, ctx2d, ownedTex } from './canvas.ts';
import { EDGE_R, LABEL_R } from './vinyl.ts';

export interface ArtOptions {
  palette?: Palette | null;
  size?: number;
}

type Art = CanvasImageSource | null;

const sizeOf = (art: Exclude<Art, null>): [number, number] => {
  if (art instanceof HTMLImageElement) return [art.naturalWidth, art.naturalHeight];
  if (art instanceof HTMLCanvasElement) return [art.width, art.height];
  if (art instanceof HTMLVideoElement) return [art.videoWidth, art.videoHeight];
  const s = art as { width: number; height: number };
  return [s.width, s.height];
};

/** Cover artwork is square; fill the target with a centre crop. */
function drawCover(g: CanvasRenderingContext2D, art: Exclude<Art, null>, size: number): void {
  const [iw, ih] = sizeOf(art);
  if (!iw || !ih) return;
  const s = Math.max(size / iw, size / ih);
  g.drawImage(art, (size - iw * s) / 2, (size - ih * s) / 2, iw * s, ih * s);
}

function drawFallback(g: CanvasRenderingContext2D, palette: Palette | null, size: number): void {
  const gr = g.createLinearGradient(0, 0, size, size);
  gr.addColorStop(0, palette?.dominant ?? '#2FBF71');
  gr.addColorStop(1, palette?.deep ?? '#14513a');
  g.fillStyle = gr;
  g.fillRect(0, 0, size, size);
}

/** Draws the artwork into a circular label with a spindle hole. */
function labelCanvas(art: Art, { palette = null, size = 512 }: ArtOptions = {}): HTMLCanvasElement {
  const c = canvas(size);
  const g = ctx2d(c);
  const R = size / 2;

  g.clearRect(0, 0, size, size);
  g.save();
  g.beginPath();
  g.arc(R, R, R, 0, TAU);
  g.clip();

  if (art) drawCover(g, art, size);
  else drawFallback(g, palette, size);

  /* A soft radial shade so it reads as printed card rather than a flat fill. */
  const shade = g.createRadialGradient(R * 0.8, R * 0.72, R * 0.1, R, R, R);
  shade.addColorStop(0, 'rgba(255,255,255,0.14)');
  shade.addColorStop(0.62, 'rgba(0,0,0,0)');
  shade.addColorStop(1, 'rgba(0,0,0,0.34)');
  g.fillStyle = shade;
  g.fillRect(0, 0, size, size);
  g.restore();

  /* Rim of the label. */
  g.strokeStyle = 'rgba(0,0,0,0.45)';
  g.lineWidth = size * 0.012;
  g.beginPath();
  g.arc(R, R, R - size * 0.006, 0, TAU);
  g.stroke();

  /* Printed ring around the spindle hole, then the hole punched through. */
  g.strokeStyle = 'rgba(255,255,255,0.30)';
  g.lineWidth = size * 0.006;
  g.beginPath();
  g.arc(R, R, size * 0.115, 0, TAU);
  g.stroke();

  g.globalCompositeOperation = 'destination-out';
  g.beginPath();
  g.arc(R, R, size * 0.037, 0, TAU);
  g.fill();
  g.globalCompositeOperation = 'source-over';

  return c;
}

export const labelTexture = (art: Art, opts: ArtOptions = {}): THREE.CanvasTexture =>
  ownedTex(labelCanvas(art, opts), 8);

/** Picture disc: artwork across the whole face, with the grooves etched over it. */
export function pictureDiscTexture(
  art: Art,
  { palette = null, size = 1024 }: ArtOptions = {},
): THREE.CanvasTexture {
  const c = canvas(size);
  const g = ctx2d(c);
  const R = size / 2;

  g.save();
  g.beginPath();
  g.arc(R, R, R, 0, TAU);
  g.clip();
  if (art) drawCover(g, art, size);
  else drawFallback(g, palette, size);

  g.globalCompositeOperation = 'multiply';
  g.lineWidth = 1;
  for (let r = R * LABEL_R + 2; r < R * EDGE_R; r += 2) {
    g.strokeStyle = 'rgba(120,124,132,0.55)';
    g.beginPath();
    g.arc(R, R, r, 0, TAU);
    g.stroke();
    g.strokeStyle = 'rgba(255,255,255,0.9)';
    g.beginPath();
    g.arc(R, R, r + 1, 0, TAU);
    g.stroke();
  }
  g.globalCompositeOperation = 'source-over';
  g.fillStyle = 'rgba(10,12,16,0.14)';
  g.beginPath();
  g.arc(R, R, R * EDGE_R, 0, TAU);
  g.arc(R, R, R * LABEL_R, 0, TAU, true);
  g.fill();
  g.restore();

  return ownedTex(c, 16);
}
